import Base from '../base/base';
import './clear-path.scss';

interface ClearPathOptions {
  rootSelector?: string;
  labelSelector?: string;
  imageSelector?: string;
  activeClass?: string;
  transitionDurationMs?: number;
  transitionScaleStart?: number;
  debug?: boolean;
}

type StepPair = {
  key: string;
  label: HTMLElement;
  image: HTMLElement;
};

export default class ClearPath extends Base {
  private config: {
    rootSelector: string;
    labelSelector: string;
    imageSelector: string;
    activeClass: string;
    transitionDurationMs: number;
    transitionScaleStart: number;
  };

  private root: HTMLElement | null = null;
  private labels: HTMLElement[] = [];
  private images: HTMLElement[] = [];
  private steps: StepPair[] = [];
  private activeIndex: number = -1;
  private isTicking: boolean = false;

  private boundOnScroll: () => void;
  private boundOnResize: () => void;

  constructor(options: ClearPathOptions = {}) {
    super(options.debug || false);

    this.config = {
      rootSelector: options.rootSelector || '.clear-path',
      labelSelector: options.labelSelector || '.process-label',
      imageSelector: options.imageSelector || '.process-image',
      activeClass: options.activeClass || 'is-active',
      transitionDurationMs: options.transitionDurationMs ?? 200,
      transitionScaleStart: options.transitionScaleStart ?? 1.02
    };

    this.boundOnScroll = this.onScroll.bind(this);
    this.boundOnResize = this.onResize.bind(this);

    this.init();
  }

  init(): void {
    this.root = document.querySelector(this.config.rootSelector);
    if (!this.root) {
      this.log('ClearPath: root not found');
      return;
    }

    this.cacheElements();
    this.buildSteps();

    if (this.steps.length === 0) {
      this.log('ClearPath: no matching steps found');
      return;
    }

    this.applyCssVariables();
    this.root.classList.add('clear-path--initialized');
    this.setActiveIndex(0);

    window.addEventListener('scroll', this.boundOnScroll, { passive: true });
    window.addEventListener('resize', this.boundOnResize, { passive: true });

    this.update();
  }

  private cacheElements(): void {
    if (!this.root) return;

    this.labels = Array.from(
      this.root.querySelectorAll<HTMLElement>(this.config.labelSelector)
    );
    this.images = Array.from(
      this.root.querySelectorAll<HTMLElement>(this.config.imageSelector)
    );
  }

  private buildSteps(): void {
    const labelMap = new Map<string, HTMLElement>();
    const imageMap = new Map<string, HTMLElement>();

    this.labels.forEach((label) => {
      const key = this.getStepKey(label);
      if (key) labelMap.set(key, label);
    });

    this.images.forEach((image) => {
      const key = this.getStepKey(image);
      if (key) imageMap.set(key, image);
    });

    const stepKeys = Array.from(labelMap.keys()).filter((key) => imageMap.has(key));
    stepKeys.sort((a, b) => this.parseStepNumber(a) - this.parseStepNumber(b));

    this.steps = stepKeys.map((key) => {
      return {
        key,
        label: labelMap.get(key) as HTMLElement,
        image: imageMap.get(key) as HTMLElement
      };
    });

    this.log('ClearPath: steps mapped', this.steps.map((step) => step.key));
  }

  private getStepKey(element: HTMLElement): string | null {
    const match = Array.from(element.classList).find((className) => {
      return /^_\d+$/.test(className);
    });

    return match || null;
  }

  private parseStepNumber(key: string): number {
    return Number.parseInt(key.replace('_', ''), 10) || 0;
  }

  private applyCssVariables(): void {
    if (!this.root) return;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    const duration = prefersReducedMotion ? 0 : this.config.transitionDurationMs;
    this.root.style.setProperty('--process-fade-duration', `${duration}ms`);
    this.root.style.setProperty('--process-scale-start', `${this.config.transitionScaleStart}`);
  }

  private onScroll(): void {
    if (this.isTicking) return;
    this.isTicking = true;
    window.requestAnimationFrame(() => this.update());
  }

  private onResize(): void {
    this.onScroll();
  }

  private update(): void {
    if (!this.root) return;

    const scrollY = window.scrollY || window.pageYOffset;
    const sectionTop = this.root.getBoundingClientRect().top + scrollY;
    const sectionHeight = this.root.offsetHeight;
    const viewportHeight = window.innerHeight || 1;
    const scrollable = Math.max(sectionHeight - viewportHeight, 1);
    const progress = (scrollY - sectionTop) / scrollable;
    const clamped = Math.min(Math.max(progress, 0), 1);
    const nextIndex = Math.min(
      this.steps.length - 1,
      Math.max(0, Math.floor(clamped * this.steps.length))
    );

    this.setActiveIndex(nextIndex);
    this.isTicking = false;
  }

  private setActiveIndex(nextIndex: number): void {
    if (nextIndex === this.activeIndex) return;

    const activeClass = this.config.activeClass;

    if (this.activeIndex >= 0) {
      const current = this.steps[this.activeIndex];
      current.label.classList.remove(activeClass);
      current.image.classList.remove(activeClass);
    }

    const next = this.steps[nextIndex];
    next.label.classList.add(activeClass);
    next.image.classList.add(activeClass);
    this.activeIndex = nextIndex;
  }
}
